import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/feature/LanguageSwitcher";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const countryLinks = [
    { labelKey: "nav.dubai", flag: "🇦🇪", path: "/dubai" },
    { labelKey: "nav.saudi", flag: "🇸🇦", path: "/saudi-arabia" },
    { labelKey: "nav.qatar", flag: "🇶🇦", path: "/qatar" },
    { labelKey: "nav.kuwait", flag: "🇰🇼", path: "/kuwait" },
    { labelKey: "nav.oman", flag: "🇴🇲", path: "/oman" },
    { labelKey: "nav.bahrain", flag: "🇧🇭", path: "/bahrain" },
    { labelKey: "nav.turkey", flag: "🇹🇷", path: "/turkey" },
    { labelKey: "nav.pakistan", flag: "🇵🇰", path: "/pakistan" },
  { labelKey: "nav.israel", flag: "🇮🇱", path: "/israel" },
  ];

  const moreLinks = [
    { labelKey: "nav.donateInfants", path: "/infant-donation", icon: "ri-heart-fill" },
    { labelKey: "nav.searchFactories", path: "/search", icon: "ri-search-line" },
    { labelKey: "nav.influencerProgram", path: "/influencer-program", icon: "ri-star-line" },
    { labelKey: "nav.howItWorks", path: "/how-it-works", icon: "ri-question-line" },
    { labelKey: "nav.registerFactory", path: "/register-factory", icon: "ri-store-2-line" },
    { labelKey: "nav.rfq", path: "/rfq", icon: "ri-file-list-3-line" },
    { labelKey: "nav.supplierDashboard", path: "/supplier-dashboard", icon: "ri-dashboard-line" },
  ];

  const desktopNavLinks = [
    { labelKey: "nav.search", path: "/search" },
    { labelKey: "nav.dubai", path: "/dubai" },
    { labelKey: "nav.saudi", path: "/saudi-arabia" },
    { labelKey: "nav.qatar", path: "/qatar" },
    { labelKey: "nav.kuwait", path: "/kuwait" },
    { labelKey: "nav.oman", path: "/oman" },
    { labelKey: "nav.bahrain", path: "/bahrain" },
    { labelKey: "nav.turkey", path: "/turkey" },
    { labelKey: "nav.pakistan", path: "/pakistan" },
    { labelKey: "nav.israel", path: "/israel" },
    { labelKey: "nav.donateInfants", path: "/infant-donation" },
    { labelKey: "nav.influencers", path: "/influencer-program" },
    { labelKey: "nav.howItWorks", path: "/how-it-works" },
    { labelKey: "nav.registerFactory", path: "/register-factory" },
    { labelKey: "nav.rfq", path: "/rfq" },
  ];

  const bgClass = isHome
    ? scrolled
      ? "bg-white shadow-sm border-b border-amber-100"
      : "bg-transparent"
    : "bg-white border-b border-amber-100";

  const textClass = isHome && !scrolled ? "text-white" : "text-gray-800";
  const logoTextClass = isHome && !scrolled ? "text-white" : "text-amber-700";

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}>
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b8685d651cf0232fd706d280f6405113.jpeg"
              alt="SuperMiddleEast Logo"
              className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border-2 border-amber-400"
            />
            <div className="hidden sm:block">
              <div className={`font-bold text-sm md:text-base leading-tight ${logoTextClass}`} style={{ fontFamily: "'Playfair Display', serif" }}>
                SuperMiddleEast
              </div>
              <div className={`text-xs opacity-75 ${textClass}`}>{t("nav.factoryBuyer")}</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-4 lg:gap-5 overflow-x-auto">
            {desktopNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-amber-500 whitespace-nowrap ${textClass}`}
              >
                {t(link.labelKey)}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            <LanguageSwitcher light={isHome && !scrolled} compact />
            <Link
              to="/supplier-dashboard"
              className={`text-sm font-medium transition-colors hover:text-amber-500 whitespace-nowrap flex items-center gap-1 ${textClass}`}
            >
              <i className="ri-dashboard-line text-sm"></i> {t("nav.dashboard")}
            </Link>
            <Link
              to="/rfq"
              className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("nav.requestQuote")}
            </Link>
          </div>

          {/* Mobile: Lang + Search + Hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher light={isHome && !scrolled} compact />
            <Link
              to="/search"
              className={`w-9 h-9 flex items-center justify-center rounded-full ${isHome && !scrolled ? "bg-white/20 text-white" : "bg-amber-50 text-amber-600"} cursor-pointer`}
            >
              <i className="ri-search-line text-lg"></i>
            </Link>
            <button
              className={`w-9 h-9 flex items-center justify-center rounded-full cursor-pointer ${isHome && !scrolled ? "bg-white/20 text-white" : "bg-amber-50 text-amber-600"}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Open menu"
            >
              <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Full-Screen Drawer */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
        />
        <div
          className={`absolute top-0 right-0 h-full w-[85vw] max-w-sm bg-white shadow-2xl flex flex-col transition-transform duration-300 ${menuOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          {/* Drawer Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-amber-100 bg-amber-50">
            <div className="flex items-center gap-3">
              <img
                src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b8685d651cf0232fd706d280f6405113.jpeg"
                alt="SuperMiddleEast"
                className="w-9 h-9 rounded-full object-cover border-2 border-amber-400"
              />
              <div>
                <div className="font-bold text-amber-700 text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>SuperMiddleEast</div>
                <div className="text-xs text-gray-400">{t("nav.factoryBuyer")}</div>
              </div>
            </div>
            <button
              onClick={() => setMenuOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto pb-6">
            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3 px-4 py-4">
              <Link
                to="/register-factory"
                onClick={() => setMenuOpen(false)}
                className="flex flex-col items-center gap-1.5 bg-amber-500 text-white rounded-2xl py-4 cursor-pointer"
              >
                <i className="ri-store-2-line text-2xl"></i>
                <span className="text-xs font-bold text-center leading-tight">{t("nav.registerFactory")}</span>
              </Link>
              <Link
                to="/rfq"
                onClick={() => setMenuOpen(false)}
                className="flex flex-col items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-2xl py-4 cursor-pointer"
              >
                <i className="ri-file-list-3-line text-2xl"></i>
                <span className="text-xs font-bold text-center leading-tight">{t("nav.requestQuote")}</span>
              </Link>
            </div>

            {/* Countries */}
            <div className="px-4 mb-2">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 px-1">{t("nav.browseByCountry")}</div>
              <div className="grid grid-cols-2 gap-2">
                {countryLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 bg-gray-50 hover:bg-amber-50 border border-gray-100 hover:border-amber-200 rounded-xl px-3 py-3 text-sm font-semibold text-gray-700 hover:text-amber-700 transition-colors cursor-pointer"
                  >
                    <span>{link.flag}</span>
                    <span>{t(link.labelKey)}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* More Links */}
            <div className="px-4 mt-4">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 px-1">{t("nav.more")}</div>
              <div className="flex flex-col gap-1">
                {moreLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-amber-50 text-gray-700 hover:text-amber-700 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                      <i className={`${link.icon} text-amber-600 text-base`}></i>
                    </div>
                    <span className="text-sm font-medium">{t(link.labelKey)}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ms-auto"></i>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="px-4 py-4 border-t border-gray-100 bg-gray-50">
            <Link
              to="/supplier-dashboard"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border-2 border-amber-400 text-amber-700 font-bold text-sm cursor-pointer"
            >
              <i className="ri-dashboard-line"></i>
              {t("nav.supplierDashboard")}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
